import Container from 'saur_ui_library/src/Atoms/Container/Container';
import Word from "saur_ui_library/src/Atoms/Word/Word";
import Button from 'saur_ui_library/src/Atoms/Input/Button/Button';
import ModalFlexible from 'saur_ui_library/src/Organisms/ModalFlexible/ModalFlexible';
import Form from 'saur_ui_library/src/Atoms/Input/Form/Form';

import { saveAs } from "file-saver";
import Loader from "./Loader";
import React, { FC, useEffect } from "react";
import styled, { createGlobalStyle } from "styled-components";
import { ExportApi, FilterApi } from "../../utils/api";
import Error from "./ErrorMessage";
import moment from "moment";
import { dateFr, maximumDate } from "../../utils/helper";
import { inputTextDate } from "../../utils/helpers/view/detailsHelper";
import { SET_JOBSLAUNCH } from "../../redux/actionType";
import { useDispatch } from "react-redux";
import ModernDatePicker from './component/ModernDatePicker';
import { MultiSelect } from 'primereact/multiselect';
import { Tree } from 'primereact/tree';
import { OverlayPanel } from 'primereact/overlaypanel';

type TreeCheckboxSelectionKeys = Record<string, { checked: boolean; partialChecked: boolean }>;
import 'primereact/resources/themes/saga-blue/theme.css';
import 'primereact/resources/primereact.min.css';
import 'primeicons/primeicons.css';

type ExportProps = {
  IsVisible: any;
  setModalVisible: any;
};

type formType = {
  startDate: null | any;
  endDate: null | any;
};

// ─── Transformation de la réponse API en nœuds PrimeReact Tree ──────────────
const buildModuleTree = (configs: any[]): any[] =>
  configs.map(m => ({
    key: m.moduleKey,
    label: m.moduleLabel,
    children: [...m.files]
      .sort((a: any, b: any) => a.sortOrder - b.sortOrder)
      .map((f: any) => ({ key: f.fileNameKey, label: f.fileLabel })),
  }));

/** Extrait uniquement les clés feuilles (sans les parents) depuis la sélection */
const extractLeafKeys = (selection: TreeCheckboxSelectionKeys, tree: any[]): string[] => {
  const parentKeys = new Set(tree.map(m => m.key));
  return Object.entries(selection)
    .filter(([key, state]) => !parentKeys.has(key) && (state as any).checked)
    .map(([key]) => key);
};

const Export: FC<ExportProps> = (props) => {
  const contractId  = window.sessionStorage.getItem('contractId')  || '';
  const contractCode = window.sessionStorage.getItem('contractCode') || '';
  const dispatch = useDispatch();

  // ─── Notification persistante (survit au démontage du composant) ──────────
  const showPersistentToast = (severity: 'info' | 'error', summary: string, detail: string) => {
    const id = 'export-persistent-notification';
    const existing = document.getElementById(id);
    if (existing) existing.remove();
    const color   = severity === 'info' ? '#008BD2' : '#d32f2f';
    const bgColor = severity === 'info' ? '#e8f4fd' : '#fdecea';
    const el = document.createElement('div');
    el.id = id;
    el.style.cssText = [
      'position:fixed', 'top:20px', 'right:20px', 'z-index:999999',
      `background:${bgColor}`, `border:1px solid ${color}`, `border-left:6px solid ${color}`,
      'padding:16px 20px', 'border-radius:4px', 'box-shadow:0 4px 12px rgba(0,0,0,.2)',
      'max-width:380px', 'font-family:montserrat-italic,sans-serif', 'cursor:pointer',
    ].join(';');
    el.innerHTML = `<div style="font-weight:bold;margin-bottom:6px;color:${color}">${summary}</div>`
      + `<div style="font-size:13px;color:#232323">${detail}</div>`;
    el.addEventListener('click', () => el.remove());
    document.body.appendChild(el);
    setTimeout(() => { if (document.getElementById(id)) el.remove(); }, 15000);
  };

  // ─── États ────────────────────────────────────────────────────────────────
  const [form, setForm] = React.useState<formType>({ startDate: null, endDate: null });
  const [installData,           setInstallData]           = React.useState<any[]>([]);
  const [selectedInstallations, setSelectedInstallations] = React.useState<any[]>([]);
  const [typeFilters, setTypeFilters] = React.useState([
    { id: 1, label: 'DO',   code: 'DO', value: false },
    { id: 2, label: 'TP',   code: 'TP', value: false },
    { id: 3, label: 'PR',   code: 'PR', value: false },
    { id: 4, label: 'MR',   code: 'MR', value: false },
    { id: 5, label: 'STEP', code: 'SE', value: false },
  ]);
  const [selectedModules,       setSelectedModules]       = React.useState<TreeCheckboxSelectionKeys>({});
  const [moduleTree,             setModuleTree]             = React.useState<any[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [error,   setError]   = React.useState(false);
  const [validation, setValidation] = React.useState({
    startDate:     null as string | null,
    endDate:       null as string | null,
    installations: null as string | null,
  });
  const allLeafKeys = moduleTree.flatMap((m: any) => m.children.map((c: any) => c.key));
  const modulePanelRef = React.useRef<any>(null);
  const [moduleExpandedKeys, setModuleExpandedKeys] = React.useState<Record<string, boolean>>({});

  // ─── Reset à l'ouverture ──────────────────────────────────────────────────
  useEffect(() => {
    setForm({ startDate: null, endDate: null });
    setSelectedInstallations([]);
    setSelectedModules({});
    setTypeFilters(prev => prev.map(t => ({ ...t, value: false })));
    setValidation({ startDate: null, endDate: null, installations: null });
    setError(false);
  }, [props.IsVisible]);

  // ─── Chargement de l'arbre des modules ──────────────────────────────────
  useEffect(() => {
    const load = async () => {
      try {
        const response = await ExportApi.getExportConfigurations();
        setModuleTree(buildModuleTree(response.data));
      } catch {
        setError(true);
      }
    };
    load();
  }, []);

  // ─── Chargement des installations ─────────────────────────────────────────
  useEffect(() => {
    if (!contractId) return;
    const activeTypes = typeFilters.filter(t => t.value).map(t => 'MDI_' + t.code);
    const installTypes = activeTypes.length > 0 ? activeTypes.join(',') : 'MDI_DO,MDI_PR,MDI_TP,MDI_MR,MDI_SE';
    const load = async () => {
      try {
        const response = await FilterApi.getInstall(contractId, installTypes);
        setInstallData(response.data);
        if (activeTypes.length === 0) {
          setSelectedInstallations([]);
        } else {
          const newCodes = new Set(response.data.map((i: any) => i.mainInstallCode));
          setSelectedInstallations(prev => prev.filter((i: any) => newCodes.has(i.mainInstallCode)));
        }
      } catch {
        setError(true);
      }
    };
    load();
  }, [contractId, typeFilters]);

  // ─── Effacement des erreurs dès que le champ est rempli ──────────────────
  useEffect(() => {
    if (form.startDate) setValidation(v => ({ ...v, startDate: null }));
  }, [form.startDate]);

  useEffect(() => {
    if (form.endDate) {
      const toMoment = (d: any) => d ? moment(`${d.year}-${d.month}-${d.day}`, 'YYYY-M-D') : null;
      const startMoment = toMoment(form.startDate);
      const endMoment   = toMoment(form.endDate);
      const endBeforeStart = startMoment && endMoment && endMoment.isBefore(startMoment);
      setValidation(v => ({ ...v, endDate: endBeforeStart ? 'La date de fin doit être égale ou supérieure à la date de début' : null }));
    }
  }, [form.endDate, form.startDate]);

  useEffect(() => {
    if (selectedInstallations.length > 0) setValidation(v => ({ ...v, installations: null }));
  }, [selectedInstallations]);

  // ─── Soumission ───────────────────────────────────────────────────────────
  const handleExport = async () => {
    const leafModules = extractLeafKeys(selectedModules as TreeCheckboxSelectionKeys, moduleTree);

    const toMoment = (d: any) => d ? moment(`${d.year}-${d.month}-${d.day}`, 'YYYY-M-D') : null;
    const startMoment = toMoment(form.startDate);
    const endMoment   = toMoment(form.endDate);
    const endBeforeStart = startMoment && endMoment && endMoment.isBefore(startMoment);

    const newValidation = {
      startDate:     form.startDate     ? null : 'Date de début manquante',
      endDate:       !form.endDate      ? 'Date de fin manquante'
                   : endBeforeStart     ? 'La date de fin doit être égale ou supérieure à la date de début'
                   : null,
      installations: selectedInstallations.length > 0 ? null : 'Sélectionnez au moins une installation',
    };
    setValidation(newValidation);

    if (Object.values(newValidation).some(v => v !== null)) return;

    const startDate = startMoment!.format('YYYY-MM-DD');
    const endDate   = endMoment!.format('YYYY-MM-DD');

    const isAll = leafModules.length === 0 || leafModules.length === allLeafKeys.length;

    const body = {
      contract_number: contractCode,
      start_date:      startDate,
      end_date:        endDate,
      installations:   selectedInstallations.map((i: any) => i.mainInstallCode),
      files:           isAll ? [] : leafModules,
    };

    try {
      setLoading(true);
      const response = await ExportApi.exportCsvByModules(body);
      if (response.status >= 200 && response.status < 300) {
        dispatch({
          type: SET_JOBSLAUNCH,
          payload: { jobId: response?.data?.numberInJob, runId: response?.data?.runId, isDatabricksJob: true },
        });
        props.setModalVisible(false);
        showPersistentToast(
          'info',
          'Lancement de l\'export',
          'Votre demande est bien prise en compte. Vous pourrez télécharger dans l\'onglet partage dans quelques minutes.',
        );
      }
    } catch {
      showPersistentToast(
        'error',
        'Export échoué',
        'Une erreur est survenue. Veuillez réessayer ou contacter votre administrateur.',
      );
    } finally {
      setLoading(false);
    }
  };

  // ─── Comptage total des fichiers + label du trigger ───────────────────────────────
  const totalFiles = moduleTree.reduce((acc: number, m: any) => acc + m.children.length, 0);

  const getModuleLabel = () => {
    const leafModules = extractLeafKeys(selectedModules, moduleTree);
    if (leafModules.length === 0) return `Tout sélectionner (${totalFiles} fichiers)`;
    return `${leafModules.length} / ${totalFiles} fichiers sélectionnés`;
  };

  // ─── Contenu du modal ─────────────────────────────────────────────────────
  const popupContent = () => (
    <Container className='modalSizeContainer'>
      <Form onSubmit={handleExport}>

        {/* ── Intervalle ── */}
        <Word variant='p' textStyle='title' value='Intervalle' className='fieldLabel' />
        <Container className='formLineDate'>
          <Container className='formLineDateItem'>
            <ModernDatePicker
              className='datePick'
              inputPlaceholder='Du'
              value={form.startDate}
              locale={dateFr}
              maximumDate={maximumDate()}
              formatInputText={() => inputTextDate(form.startDate)}
              onChange={(date) => setForm(v => ({ ...v, startDate: date }))}
            />
          </Container>
          <Container className='formLineDateItem'>
            <ModernDatePicker
              className='datePick'
              inputPlaceholder='Au'
              value={form.endDate}
              locale={dateFr}
              minimumDate={form.startDate}
              maximumDate={maximumDate()}
              formatInputText={() => inputTextDate(form.endDate)}
              onChange={(date) => setForm(v => ({ ...v, endDate: date }))}
            />
          </Container>
        </Container>
        <Container className='formLineError'>
          <Container className='formLineDateErrorItem'>
            <Word className='error' textStyle='normalReg' variant='span' value={validation.startDate ?? ''} />
          </Container>
          <Container className='formLineDateErrorItem'>
            <Word className='error' textStyle='normalReg' variant='span' value={validation.endDate ?? ''} />
          </Container>
        </Container>

        {/* ── Filtre type ── */}
        <Word variant='p' textStyle='title' value='Type' className='fieldLabel' />
        <Container className='formLineType sds-reset-style'>
          {typeFilters.map((t) => (
            <Container key={t.id} className='formLineTypeItem'>
              <label className='type-filter-checkbox'>
                <input
                  type='checkbox'
                  checked={t.value}
                  onChange={(e) =>
                    setTypeFilters(prev => prev.map(f => f.id === t.id ? { ...f, value: e.target.checked } : f))
                  }
                />
                <span className='type-filter-box'>✓</span>
              </label>
              <Word className='label-chckb' textStyle='normalMed' value={t.label} variant='span' />
            </Container>
          ))}
        </Container>

        {/* ── Installations ── */}
        <Word variant='p' textStyle='title' value='Installations' className='fieldLabel' />
        <Container className='formLineInstall sds-reset-style'>
          <GlobalTreeStyles />
          <MultiSelect
            className='export-multiselect'
            value={selectedInstallations}
            options={installData}
            onChange={(e) => setSelectedInstallations(e.value)}
            optionLabel='mainInstallLabel'
            placeholder='Sélectionnez les installations'
            display='chip'
            filter
            filterPlaceholder='Rechercher...'
            emptyFilterMessage='Aucune installation trouvée'
          />
        </Container>
        <Container className='formLineError'>
          <Word className='error' textStyle='normalReg' variant='span' value={validation.installations ?? ''} />
        </Container>

        {/* ── Modules ── */}
        <Word variant='p' textStyle='title' value='Modules' className='fieldLabel' />
        <Container className='formLineInstall sds-reset-style'>
          <GlobalTreeStyles />
          <div
            className='module-tree-trigger'
            onClick={(e) => modulePanelRef.current?.toggle(e)}
          >
            <span className='module-tree-trigger-label'>{getModuleLabel()}</span>
            <span className='pi pi-chevron-down module-tree-trigger-icon' />
          </div>
          <OverlayPanel ref={modulePanelRef} className='module-tree-panel'>
            <div
              onClick={(e) => e.stopPropagation()}
              onMouseDown={(e) => e.stopPropagation()}
            >
              <Tree
                value={moduleTree}
                selectionMode='checkbox'
                selectionKeys={selectedModules}
                onSelectionChange={(e) => setSelectedModules(e.value as TreeCheckboxSelectionKeys)}
                expandedKeys={moduleExpandedKeys}
                onToggle={(e) => setModuleExpandedKeys(e.value)}
              />
            </div>
          </OverlayPanel>
        </Container>
        {/* ── Bouton ── */}
        <Container className='formLineButtonItem'>
          <StyledSectionButton>
            <Button className='export-btn' mode='plain'>
              <Word variant='span' textStyle='normalBold' value='Exporter' />
            </Button>
          </StyledSectionButton>
        </Container>

      </Form>
    </Container>
  );

  return (
    <Container>
      <Styles />
      <ModalFlexible
        showHeader
        className='exportContainer'
        title='Export de données'
        toggleModal={props.setModalVisible}
        visible={props.IsVisible}
        desktopContent={popupContent}
      />
      {loading ? (
        <Container automatedTestId='loader' className='loader'>
          <Loader IsVisible={true} />
        </Container>
      ) : null}
      {error ? <Error IsVisible={error} /> : null}
    </Container>
  );
};

const StyledSectionButton = styled(Container)`
  flex-direction: row;
  justify-content: space-around;
`;

const GlobalTreeStyles = createGlobalStyle`
  .sds-reset-style {
    .pi { font-family: 'primeicons'; }
  }
  .export-multiselect {
    width: 100%;
    border-radius: 0;
    background-color: #edf2fa;
    border-color: #edf2fa;
    font-size: 14px;
    font-family: 'montserrat-italic';
    min-height: 42px;
    align-items: flex-start;
  }
  .export-multiselect .p-multiselect-label-container {
    max-height: 120px;
    overflow-y: auto;
    overflow-x: hidden;
  }
  .export-multiselect .p-multiselect-label {
    display: flex;
    flex-wrap: wrap;
    white-space: normal;
    padding: 6px 4px;
  }
  .export-multiselect .p-multiselect-trigger {
    color: #008BD2;
    width: 20px;
  }
  .export-multiselect .p-multiselect-label.p-placeholder {
    color: #232323;
    line-height: 2.5;
    padding-left: 10px;
  }
  .p-multiselect-token {
    margin: 2px 4px;
    padding: 2px 8px;
    background-color: #008BD2;
    color: #fff;
    border-radius: 12px;
    font-size: 12px;
  }
  /* ── Trigger module ── */
  .module-tree-trigger {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    min-height: 42px;
    background-color: #edf2fa;
    border: 1px solid #edf2fa;
    padding: 0 10px;
    cursor: pointer;
    font-size: 14px;
    font-family: 'montserrat-italic';
    color: #232323;
    box-sizing: border-box;
  }
  .module-tree-trigger-label {
    flex: 1;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .module-tree-trigger-icon {
    color: #008BD2;
    font-size: 12px;
    margin-left: 8px;
  }
  /* ── Panel OverlayPanel ── */
  .module-tree-panel {
    z-index: 9999 !important;
  }
  .module-tree-panel .p-overlaypanel-content {
    padding: 0;
    max-height: 320px;
    overflow-y: auto;
  }
  .p-tree .p-tree-container .p-treenode .p-treenode-content {
    padding: 4px 8px;
  }
  .p-tree .p-tree-container .p-treenode .p-treenode-content:focus {
    outline: none;
    box-shadow: none;
  }
  .p-tree > .p-tree-container > .p-treenode > .p-treenode-content .p-treenode-label {
    font-weight: bold;
  }
  .p-checkbox .p-checkbox-box.p-highlight {
    background: #008BD2 !important;
    border-color: #008BD2 !important;
  }
`;

const Styles = createGlobalStyle`
.sds-reset-style {
  .exportContainer {
    z-index: 200;
    div.modal-flex-desktop-content {
      width: 100vh;
    }
    .modalSizeContainer {
      margin-bottom: 2%;
      color: black !important;
      .formLineDate {
        flex-direction: row;
        justify-content: space-between;
        margin-bottom: 4px;
      }
      .formLineType {
        flex-direction: row;
        flex-wrap: wrap;
        margin-bottom: 15px;
      }
      .formLineTypeItem {
        flex-direction: row;
        align-items: center;
        width: 20%;
      }
      .type-filter-checkbox {
        display: inline-flex;
        align-items: center;
        cursor: pointer;
        margin: 0;
      }
      .type-filter-checkbox input[type="checkbox"] {
        display: none;
      }
      .type-filter-box {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 18px;
        height: 18px;
        min-width: 18px;
        min-height: 18px;
        border: 2px solid #008BD2;
        border-radius: 3px;
        background-color: #fff;
        box-sizing: border-box;
        color: transparent;
        font-size: 13px;
        font-weight: bold;
        line-height: 1;
      }
      .type-filter-checkbox input[type="checkbox"]:checked + .type-filter-box {
        background-color: #008BD2;
        border-color: #008BD2;
        color: #fff;
      }
      .label-chckb {
        margin-left: 5%;
      }
      .formLineInstall {
        flex-direction: row;
        margin-bottom: 15px;
        width: 100%;
      }
      .formLineDateItem {
        align-items: center;
        position: relative;
        background-color: #edf2fa;
        width: 48%;
      }
      .formLineButtonItem {
        align-items: center;
        margin-right: 4%;
        margin-top: 25px;
        .export-btn {
          margin-top: 20px;
        }
      }
      .formLineError {
        flex-direction: row;
        justify-content: space-between;
        margin-bottom: 15px;
        height: 20px;
        .error { color: red; }
      }
      .formLineDateErrorItem {
        align-items: center;
        position: relative;
        width: 48%;
      }
      .fieldLabel {
        margin-bottom: 8px;
      }
      .datePick {
        background-color: #edf2fa;
        border-color: #edf2fa;
      }
      .sds-m-modern-date-picker {
        margin-bottom: 0px !important;
      }
      .sds-m-modern-date-picker .my-custom-input {
        background-color: #edf2fa;
        border-color: #edf2fa;
      }
      .dropdown-icon-date {
        position: absolute;
        right: 20px;
        width: 28px;
        height: 28px;
        top: 50%;
        transform: translateY(-50%);
        z-index: 99;
      }
      .dropdown-icon-container svg { width: 15px; }
    }
  }
}
.modal-flex-desktop .modal-flex-desktop-overlay .modal-flex-desktop-position {
  height: fit-content !important;
  align-self: center;
}
.modal-flex-desktop .modal-flex-desktop-overlay .modal-flex-desktop-position .modal-flex-desktop-content {
  height: fit-content !important;
}
.modal-flex-desktop .modal-flex-desktop-overlay .modal-flex-desktop-position .modal-flex-desktop-content .modal-flex-desktop-header {
  align-items: flex-end !important;
  padding: 0px !important;
  height: max-content;
  flex-direction: column !important;
}
.sds-word.title.modal-flex-desktop-content-title {
  color: black !important;
}
.modal-flex-mobile-header { display: none; }
`;

export default Export;
